// ===========================================
// Cookie Consent Banner Component
// ===========================================
// GDPR + Australian Privacy Act compliant cookie banner
// Displays at bottom of screen, hidden after consent

import { useState } from 'react';
import {
  Box,
  Button,
  Card,
  Container,
  Divider,
  Link as MuiLink,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import { useCookieConsent } from '../contexts/CookieConsentContext';

export default function CookieBanner() {
  const { state, acceptAll, rejectNonEssential, updateConsent, saveConsent } = useCookieConsent();
  const [showPreferences, setShowPreferences] = useState(false);

  // Hide banner if consent has been given
  if (state.hasConsented) {
    return null;
  }

  const handleSavePreferences = () => {
    saveConsent();
    setShowPreferences(false);
  };

  return (
    <Box
      component="section"
      aria-label="Cookie consent"
      sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 1300, // Above most UI, below modals
        backgroundColor: 'background.paper',
        borderTop: '4px solid',
        borderColor: 'primary.main',
      }}
    >
      <Container maxWidth="lg" sx={{ py: 2 }}>
        {!showPreferences ? (
          <Card
            sx={{
              p: 2,
              backgroundColor: '#FCF6E6', // Cream background from brand palette
              border: 'none',
              boxShadow: 'none',
            }}
          >
            <Stack spacing={2}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  We use cookies
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  We use cookies to enhance your experience, remember your preferences, and analyze
                  how you use our site. Essential cookies are always enabled. By continuing, you
                  consent to our use of cookies as described in our{' '}
                  <MuiLink href="/legal/music-n-me/privacy" underline="hover" target="_blank">
                    Privacy Policy
                  </MuiLink>
                  {' and '}
                  <MuiLink href="/legal/music-n-me/cookies" underline="hover" target="_blank">
                    Cookie Policy
                  </MuiLink>
                  .
                </Typography>
              </Box>

              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1}
                sx={{
                  justifyContent: 'flex-end',
                }}
              >
                <Button
                  variant="outlined"
                  color="inherit"
                  onClick={() => setShowPreferences(true)}
                  size="small"
                >
                  Manage Preferences
                </Button>
                <Button variant="outlined" color="primary" onClick={rejectNonEssential} size="small">
                  Reject Non-Essential
                </Button>
                <Button variant="contained" color="primary" onClick={acceptAll} size="small">
                  Accept All
                </Button>
              </Stack>
            </Stack>
          </Card>
        ) : (
          <Card
            sx={{
              p: 2,
              backgroundColor: '#FCF6E6',
              border: 'none',
              boxShadow: 'none',
            }}
          >
            <Stack spacing={2}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Cookie Preferences
                </Typography>
              </Box>

              {/* Essential Cookies */}
              <Box>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    py: 1,
                  }}
                >
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      Essential Cookies
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Required for site functionality and security
                    </Typography>
                  </Box>
                  <Switch checked={true} disabled />
                </Box>
              </Box>

              <Divider />

              {/* Analytics Cookies */}
              <Box>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    py: 1,
                  }}
                >
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      Analytics Cookies
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Help us understand how you use our site
                    </Typography>
                  </Box>
                  <Switch
                    checked={state.analytics}
                    onChange={(e) => updateConsent('analytics', e.target.checked)}
                  />
                </Box>
              </Box>

              <Divider />

              {/* Marketing Cookies */}
              <Box>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    py: 1,
                  }}
                >
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      Marketing Cookies
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Used to track your interests and deliver personalized ads
                    </Typography>
                  </Box>
                  <Switch
                    checked={state.marketing}
                    onChange={(e) => updateConsent('marketing', e.target.checked)}
                  />
                </Box>
              </Box>

              <Divider />

              {/* Preferences Cookies */}
              <Box>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    py: 1,
                  }}
                >
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      Preference Cookies
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Remember your settings and preferences
                    </Typography>
                  </Box>
                  <Switch
                    checked={state.preferences}
                    onChange={(e) => updateConsent('preferences', e.target.checked)}
                  />
                </Box>
              </Box>

              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1}
                sx={{
                  justifyContent: 'flex-end',
                  pt: 1,
                }}
              >
                <Button
                  variant="outlined"
                  color="inherit"
                  onClick={() => setShowPreferences(false)}
                  size="small"
                >
                  Back
                </Button>
                <Button variant="contained" color="primary" onClick={handleSavePreferences} size="small">
                  Save Preferences
                </Button>
              </Stack>
            </Stack>
          </Card>
        )}
      </Container>
    </Box>
  );
}
